import React, { useEffect, useMemo } from 'react';
import { useIntl } from '../../util/reactIntl';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';
import { computeBundlePricing, getBrandDisplayName } from '../../util/bundlePricing';
import { Modal, PrimaryButton } from '../../components';
import { BundleProductCard } from '../../containers/ListingPage/SectionBundles';
import { hideTidioChat, showTidioChat } from '../../util/tidioChat';

import css from './BundleDetailModal.module.css';

const { Money } = sdkTypes;

/**
 * Modal showing full bundle details: brand + title header (matching listing page
 * typography), products list, price with optional discount badge, and add-to-cart CTA.
 */
const BundleDetailModal = ({
  bundle,
  productListingsById = {},
  isOpen,
  onClose,
  onAddBundleToCart,
  isAdding = false,
  config,
  onManageDisableScrolling,
}) => {
  const intl = useIntl();
  const variantPrefix = config?.layout?.listingImage?.variantPrefix || 'listing-card';

  useEffect(() => {
    if (!isOpen) return;
    hideTidioChat();
    return () => showTidioChat();
  }, [isOpen]);

  const pricing = useMemo(
    () => (bundle ? computeBundlePricing(bundle, productListingsById) : null),
    [bundle, productListingsById]
  );

  const finalMoney = useMemo(() => {
    if (!pricing?.currency || !Number.isFinite(pricing?.finalAmount)) return null;
    try { return new Money(pricing.finalAmount, pricing.currency); } catch { return null; }
  }, [pricing]);

  const rawMoney = useMemo(() => {
    if (!pricing?.showStrikethrough || !pricing?.currency || !Number.isFinite(pricing?.rawSubtotal)) return null;
    try { return new Money(pricing.rawSubtotal, pricing.currency); } catch { return null; }
  }, [pricing]);

  const badgeFormatted = useMemo(() => {
    if (!pricing?.badgeDiscountSubunits || pricing.badgeDiscountSubunits <= 0 || !pricing?.currency) return null;
    try { return formatMoney(intl, new Money(pricing.badgeDiscountSubunits, pricing.currency)); } catch { return null; }
  }, [intl, pricing]);

  // Brand name if all products share the same brand
  const singleBrand = useMemo(() => {
    if (!pricing) return null;
    const brands = pricing.productsWithListings
      .map(({ listing }) => listing?.attributes?.publicData?.brand)
      .filter(Boolean)
      .map(b => (Array.isArray(b) ? b[0] : b))
      .filter(b => typeof b === 'string' && b);
    if (brands.length === 0) return null;
    const unique = [...new Set(brands)];
    if (unique.length !== 1) return null;
    return getBrandDisplayName(unique[0], config?.listing);
  }, [pricing, config]);

  if (!bundle) return null;

  const disabled = !finalMoney || isAdding;
  const isMultiProduct = (pricing?.productsWithListings?.length || 0) > 2;

  return (
    <Modal
      id="BundleDetailModal"
      isOpen={isOpen}
      onClose={onClose}
      onManageDisableScrolling={onManageDisableScrolling || (() => {})}
      scrollLayerClassName={css.modalScrollLayer}
      containerClassName={css.modalContainer}
      usePortal
    >
      <div className={css.body}>
        {/* Header — brand + title, right-aligned like the product page right column */}
        <header className={css.header}>
          {singleBrand ? <p className={css.brandLabel}>{singleBrand}</p> : null}
          <h2 className={css.title}>{bundle.title}</h2>
          {bundle.description ? (
            <p className={css.description}>{bundle.description}</p>
          ) : null}
        </header>

        {/* Products */}
        <div className={css.productsSection}>
          <p className={css.productsLabel}>
            {intl.formatMessage({
              id: 'BundleDetailModal.productsHeading',
              defaultMessage: 'Produits inclus',
            })}
          </p>

          {isMultiProduct ? (
            (() => {
              const products = pricing.productsWithListings;
              const rows = [];
              for (let i = 0; i < products.length; i += 2) rows.push(products.slice(i, i + 2));
              return (
                <div className={css.productGrid}>
                  {rows.map((row, rowIdx) => (
                    <React.Fragment key={`row-${rowIdx}`}>
                      {rowIdx > 0 ? (
                        <div className={css.rowSep}>
                          <span className={css.plusSymbol} aria-hidden="true">+</span>
                        </div>
                      ) : null}
                      <div className={css.gridRow}>
                        {row.map(({ product, listing, money }, colIdx) => {
                          const qty = Math.max(1, Number(product.quantity) || 1);
                          return (
                            <React.Fragment key={`${bundle.id}-${product.id}-${rowIdx * 2 + colIdx}`}>
                              {colIdx > 0 ? <span className={css.plusSymbol} aria-hidden="true">+</span> : null}
                              {qty > 1 ? <span className={css.qtySymbol} aria-hidden="true">{qty}&nbsp;×</span> : null}
                              <BundleProductCard product={product} listing={listing} money={money} intl={intl} config={config} />
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              );
            })()
          ) : (
            <div className={css.productRow}>
              {pricing.productsWithListings.map(({ product, listing, money }, idx) => {
                const qty = Math.max(1, Number(product.quantity) || 1);
                return (
                  <React.Fragment key={`${bundle.id}-${product.id}-${idx}`}>
                    {idx > 0 ? <span className={css.plusSymbol} aria-hidden="true">+</span> : null}
                    {qty > 1 ? <span className={css.qtySymbol} aria-hidden="true">{qty}&nbsp;×</span> : null}
                    <BundleProductCard product={product} listing={listing} money={money} intl={intl} config={config} />
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className={css.footer}>
          <div className={css.priceBlock}>
            {finalMoney && pricing.showStrikethrough && rawMoney ? (
              <div className={css.priceWithSale}>
                <span className={css.finalPrice}>{formatMoney(intl, finalMoney)}</span>
                <div className={css.strikethroughStack}>
                  {badgeFormatted ? (
                    <span className={css.badgeInline}>-{badgeFormatted}</span>
                  ) : null}
                  <span className={css.oldPrice}>{formatMoney(intl, rawMoney)}</span>
                </div>
              </div>
            ) : finalMoney ? (
              <span className={css.finalPrice}>{formatMoney(intl, finalMoney)}</span>
            ) : (
              <span className={css.pricePlaceholder}>—</span>
            )}
          </div>

          <PrimaryButton
            className={css.addToCartButton}
            onClick={() => !disabled && onAddBundleToCart && onAddBundleToCart(bundle)}
            disabled={disabled}
            inProgress={isAdding}
            type="button"
          >
            {intl.formatMessage({
              id: 'BundleDetailModal.addToCart',
              defaultMessage: 'Ajouter au panier',
            })}
          </PrimaryButton>
        </footer>
      </div>
    </Modal>
  );
};

export default BundleDetailModal;
